import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import DashboardLayout from "./components/layout/DashboardLayout";
import LoginPage from "./pages/LoginPage";
import MyDashboard from "./pages/MyDashboard";
import TransactionsPage from "./pages/TransactionsPage";
import TransactionDetailPage from "./pages/TransactionDetailPage";
import ImportCenterPage from "./pages/ImportCenterPage";
import InboxPage from "./pages/InboxPage";
import AdminDashboardPage from "./pages/AdminDashboardPage.jsx";
import MetricDetailPage from "./pages/MetricDetailPage.jsx";
import ExpensesPage from "./pages/ExpensesPage.jsx";
import VendorsPage from "./pages/VendorsPage";
import LoansPage from "./pages/LoansPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/ProfilePage";
import ReconciliationPage from "./pages/ReconciliationPage";
import DocumentAnalyzerPage from "./pages/DocumentAnalyzerPage.jsx";
import EcobankLetterPage from "./pages/EcobankLetterPage.jsx";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-emerald-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<MyDashboard />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="transactions/:id" element={<TransactionDetailPage />} />
        <Route
          path="import"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager"]}>
              <ImportCenterPage />
            </ProtectedRoute>
          }
        />
        <Route path="inbox" element={<InboxPage />} />
        <Route
          path="analytics"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager"]}>
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="analytics/expenses"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager"]}>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="analytics/:metricType"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager"]}>
              <MetricDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="loans"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager"]}>
              <LoansPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager"]}>
              <VendorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager"]}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reconciliation"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager"]}>
              <ReconciliationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="document-analyzer"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager"]}>
              <DocumentAnalyzerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ecobank-letter"
          element={
            <ProtectedRoute allowedRoles={["admin", "manager"]}>
              <EcobankLetterPage />
            </ProtectedRoute>
          }
        />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
